import { ColorEnum } from '@/constants/color'
import useCatalogSearch from '@/features/openTabs/components/ProductCatalog/hooks/useCatalogSearch'
import CatalogHeader from '@/features/openTabs/components/ProductCatalog/parts/CatalogHeader'
import CategoryList from '@/features/openTabs/components/ProductCatalog/parts/CategoryList'
import ProductSection from '@/features/openTabs/components/ProductCatalog/parts/ProductSection'
import type { CatalogCategory, CatalogProduct, CatalogSection } from '@/features/openTabs/types'
import { useMeasureNumColumns } from '@/features/pos/components/ProductCatalog/parts/ProductList/hooks/useMeasureNumColumns'
import Typography from '@/features/ui/typography'
import { useScreenLayout, useScreenOrientation, useScreenSize } from '@/hooks/screen'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export type { CatalogCategory, CatalogProduct, CatalogSection }

interface ProductCatalogProps {
    disabled?: boolean
    categories: readonly CatalogCategory[]
    sections: readonly CatalogSection[]
    selectedCategoryId: string
    selectedProductIds?: readonly string[]
    onCategorySelect: (categoryId: string) => void
    onProductSelect: (productId: string) => void
}

const ProductCatalog: FC<ProductCatalogProps> = ({
    disabled = false,
    categories,
    sections,
    selectedCategoryId,
    selectedProductIds = [],
    onCategorySelect,
    onProductSelect,
}) => {
    const { t } = useTranslation('app')
    const layout = useScreenLayout()
    const screenSize = useScreenSize()
    const screenOrientation = useScreenOrientation()
    const scrollControls = screenSize === 'small' && screenOrientation === 'landscape'
    const { styles } = useStyles(stylesheet, {
        layout: layout === 'compact' ? 'compact' : undefined,
    })
    const [measuredNumColumns, measureProductList] = useMeasureNumColumns('grid')
    const numColumns = measuredNumColumns ?? 1
    const {
        searchOpen,
        searchValue,
        searching,
        visibleSections,
        openSearch,
        closeSearch,
        setSearchValue,
    } = useCatalogSearch(sections, selectedCategoryId)
    const selectedProductIdSet = useMemo(() => new Set(selectedProductIds), [selectedProductIds])
    const header = (
        <CatalogHeader
            searchOpen={searchOpen}
            searchValue={searchValue}
            onSearchOpen={openSearch}
            onSearchClose={closeSearch}
            onSearchChange={setSearchValue}
        />
    )
    const categoryList = !searching && (
        <CategoryList
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            horizontal={layout === 'compact'}
            onSelect={onCategorySelect}
        />
    )

    return (
        <View style={styles.container}>
            {!scrollControls && header}
            <View style={styles.content}>
                {!scrollControls && categoryList}
                <ScrollView
                    key={searching && !scrollControls ? 'search' : selectedCategoryId}
                    onLayout={measureProductList}
                    contentContainerStyle={styles.productListContent}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                    style={styles.productList}
                >
                    {scrollControls && (
                        <>
                            {header}
                            {categoryList}
                        </>
                    )}
                    {searching && visibleSections.length === 0 && (
                        <Typography.Description>
                            {t('openTabs.catalog.emptySearch')}
                        </Typography.Description>
                    )}
                    {visibleSections.map((section) => (
                        <ProductSection
                            key={section.id}
                            disabled={disabled}
                            section={section}
                            numColumns={numColumns}
                            selectedProductIds={selectedProductIdSet}
                            onSelect={onProductSelect}
                        />
                    ))}
                </ScrollView>
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        flex: 1,
        minWidth: 0,
        backgroundColor: ColorEnum.WHITE,
        boxShadow: '2px 0px 8px rgba(35, 31, 32, 0.12)',
    },
    content: {
        flex: 1,
        flexDirection: 'row',
        gap: 16,
        paddingHorizontal: 16,
        paddingTop: 16,
        minHeight: 0,
        variants: {
            layout: {
                compact: {
                    flexDirection: 'column',
                },
            },
        },
    },
    productList: {
        flex: 1,
        minHeight: 0,
    },
    productListContent: {
        gap: 16,
        paddingBottom: 16,
    },
})

export default ProductCatalog
